import { useMemo } from 'react';

import { findItemNode } from '../utils/pro-tree';
import { useProTreeQuery } from './use-questionnaire-tree';

/**
 * The Scope a question inherits from the PRO item it belongs to (SHCT-517 / [ADM.046]).
 *
 * PRD 05 v1.29 §3.1 — 문항은 자체 적용범위를 갖지 않고 소속 항목의 Scope를 상속하며, 문항 에디터에는
 * **수정 불가 텍스트**로 표시한다. The owner of the value stays PRD 04's item form.
 *
 * Read straight off the questionnaire tree: `ProTreeResponse.Item` now carries
 * `scope` · `diseaseGroupId` · `diseaseCodeId`, which is the BE dependency [ADM.046] named. The tree
 * is already loaded for the panel, so the inherited Scope costs no extra request — and the disease
 * filter (SHCT-512) reads the same field, so the editor and the filter can never disagree.
 */
export interface InheritedItemScope {
  scope: string;
  diseaseGroupId: number | null;
  diseaseCodeId: number | null;
}

export function useInheritedItemScope(selectedItemId: string | null) {
  const { data: tree, isLoading } = useProTreeQuery();

  const item = useMemo(
    () => (tree && selectedItemId ? findItemNode(tree, selectedItemId) : null),
    [tree, selectedItemId],
  );

  const scope: InheritedItemScope | null = useMemo(() => {
    if (!item?.scope) return null;
    return {
      scope: item.scope,
      diseaseGroupId: item.diseaseGroupId ?? null,
      diseaseCodeId: item.diseaseCodeId ?? null,
    };
  }, [item]);

  return {
    scope,
    isLoading,
    // The tree loaded but the item carries no `scope` — a server build older than the [ADM.046]
    // field. The caller then falls back to the question's own stored values and drops the
    // "inherited from the item" hint, rather than captioning a value that did not come from there.
    isUnavailable: !isLoading && !!item && !item.scope,
  };
}
